import type { FC } from 'react';

import { CONTACT } from '@/constants/site.constants';
import { SECTION_IDS } from '@/features/home/constants/home.constants';
import { SITE_CONTAINER } from '@/features/home/constants/homeStyles.constants';
import type { FaqItem } from '@/types/home.types';

interface FaqSectionProps {
  items: FaqItem[];
  openIndex: number;
  onToggle: (index: number) => void;
}

const FaqSection: FC<FaqSectionProps> = ({ items, openIndex, onToggle }) => (
  <section id={SECTION_IDS.faq} className="scroll-mt-20 bg-site-bg">
    <div
      className={`${SITE_CONTAINER} grid grid-cols-[repeat(auto-fit,minmax(min(100%,320px),1fr))] items-start gap-10 pb-16 md:pb-[88px]`}
    >
      <div>
        <span className="block font-site text-[13px] tracking-[0.08em] text-site-accent-deep uppercase">
          FAQ
        </span>
        <h2 className="mt-4 font-site text-[clamp(28px,3vw,40px)] leading-[1.1] font-extrabold tracking-[-0.02em]">
          Questions students ask us
        </h2>
        <p className="mt-4 font-site text-[15px] leading-[1.5] text-site-ink/80">
          Something else? Write to{' '}
          <a href={`mailto:${CONTACT.email}`} className="text-site-accent hover:underline">
            {CONTACT.email}
          </a>
        </p>
      </div>

      <div className="border-b-2 border-site-ink">
        {items.map((item, index) => {
          const isOpen = index === openIndex;
          const panelId = `faq-panel-${index}`;

          return (
            <div key={item.question} className="border-t-2 border-site-ink">
              <button
                type="button"
                onClick={() => onToggle(index)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                className="flex w-full cursor-pointer justify-between gap-4 py-[18px] text-left font-site text-lg leading-[1.35] font-bold transition-colors hover:text-site-accent"
              >
                <span>{item.question}</span>
                <span aria-hidden className="shrink-0 font-extrabold text-site-accent">
                  {isOpen ? '−' : '+'}
                </span>
              </button>

              {isOpen ? (
                <p
                  id={panelId}
                  className="pr-10 pb-5 font-site text-base leading-[1.55] text-site-ink/80"
                >
                  {item.answer}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

export default FaqSection;
